import { useState, useEffect, useRef } from "react";
import { Search, ChevronDown } from "lucide-react";
import { useGeoData, GeoCountry } from "@/hooks/useGeoData";
import { getPhoneCode } from "@/lib/countryPhoneCodes";
import { cn } from "@/lib/utils";

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  onCountryChange?: (country: GeoCountry, code: string) => void;
  selectedCountry?: GeoCountry | null;
  countryCode?: string;
  error?: string | null;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

export const PhoneInput = ({
  value,
  onChange,
  onCountryChange,
  selectedCountry: externalSelectedCountry,
  countryCode: externalCountryCode,
  error,
  disabled = false,
  placeholder = "Enter phone number",
  className,
  autoFocus = false,
}: PhoneInputProps) => {
  const { getCountries, getCountryFlag } = useGeoData();
  const [countries, setCountries] = useState<GeoCountry[]>([]);
  const [internalSelectedCountry, setInternalSelectedCountry] = useState<GeoCountry | null>(null);
  const [internalCountryCode, setInternalCountryCode] = useState("+1");
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const countryPickerRef = useRef<HTMLDivElement>(null);

  // Use external state if provided, otherwise use internal state
  const selectedCountry = externalSelectedCountry ?? internalSelectedCountry;
  const countryCode = externalCountryCode ?? internalCountryCode;

  // Load countries on mount
  useEffect(() => {
    let isMounted = true;
    const loadCountries = async () => {
      const allCountries = await getCountries();
      if (!isMounted) return;
      setCountries(allCountries);
      // Default to US if no country is selected
      if (!selectedCountry) {
        const usCountry = allCountries.find((c) => c.iso2 === "US");
        if (usCountry) {
          setInternalSelectedCountry(usCountry);
          const code = getPhoneCode(usCountry.iso2);
          setInternalCountryCode(code);
          onCountryChange?.(usCountry, code);
        }
      }
    };
    loadCountries();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close country picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        countryPickerRef.current &&
        !countryPickerRef.current.contains(target) &&
        !target.closest('.phone-input-country-picker')
      ) {
        setShowCountryPicker(false);
      }
    };

    if (showCountryPicker) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showCountryPicker]);

  // Auto-focus phone input if requested
  useEffect(() => {
    if (autoFocus && selectedCountry && !showCountryPicker) {
      setTimeout(() => {
        phoneInputRef.current?.focus();
      }, 100);
    }
  }, [autoFocus, selectedCountry, showCountryPicker]);

  const filteredCountries = countries.filter((country) =>
    country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    country.iso2.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const handleCountrySelect = (country: GeoCountry) => {
    const newCode = getPhoneCode(country.iso2);
    
    // Update internal state if not controlled externally
    if (!externalSelectedCountry) {
      setInternalSelectedCountry(country);
      setInternalCountryCode(newCode);
    }
    
    // Always call the callback
    onCountryChange?.(country, newCode);
    
    setShowCountryPicker(false);
    setCountrySearch("");
    
    // Auto-focus phone input after selection
    setTimeout(() => {
      phoneInputRef.current?.focus();
    }, 100);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits
    const digits = e.target.value.replace(/\D/g, "");
    onChange(digits);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent backspace from deleting the prefix
    if (e.key === "Backspace" && value.length === 0) {
      e.preventDefault();
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      {/* Single Unified Phone Input Field */}
      <div className="relative" ref={countryPickerRef}>
        <div
          className={cn(
            "flex items-center rounded-xl bg-muted border-0 focus-within:ring-2 focus-within:ring-[hsl(var(--romantic-primary))] transition-all overflow-hidden",
            error && "ring-2 ring-destructive",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        >
          {/* Country Flag Selector Button (Left Side) */}
          <button
            type="button"
            onClick={() => !disabled && setShowCountryPicker(!showCountryPicker)}
            disabled={disabled}
            className={cn(
              "flex items-center gap-2 px-3 py-3 flex-shrink-0 border-r border-border/50",
              "hover:bg-muted/80 transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-[hsl(var(--romantic-primary))] focus:ring-offset-2",
              disabled && "cursor-not-allowed"
            )}
            aria-label="Select country"
          >
            {selectedCountry ? (
              <>
                <span className="text-xl leading-none">{getCountryFlag(selectedCountry.iso2)}</span>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </>
            ) : (
              <span className="text-sm text-muted-foreground">Select</span>
            )}
          </button>

          {/* Dialing Code Prefix (Non-editable) */}
          <div className="flex items-center px-3 py-3 border-r border-border/50 bg-muted/50 flex-shrink-0">
            <span className="text-sm font-medium text-foreground whitespace-nowrap">
              {countryCode || "+1"}
            </span>
          </div>

          {/* Phone Number Input */}
          <input
            ref={phoneInputRef}
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            value={value}
            onChange={handlePhoneChange}
            onKeyDown={handleKeyDown}
            placeholder={selectedCountry ? placeholder : "Select country first"}
            disabled={!selectedCountry || disabled}
            className={cn(
              "flex-1 pl-4 pr-4 py-3 bg-transparent border-0 outline-none",
              "placeholder:text-muted-foreground",
              "text-base",
              (!selectedCountry || disabled) && "cursor-not-allowed opacity-50"
            )}
            aria-label="Phone number"
            aria-invalid={!!error}
            aria-describedby={error ? "phone-error" : undefined}
          />
        </div>

        {/* Country Picker Dropdown */}
        {showCountryPicker && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-card rounded-xl shadow-lg border border-border z-50 max-h-80 overflow-hidden flex flex-col phone-input-country-picker">
            {/* Search */}
            <div className="p-3 border-b border-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search countries..."
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg bg-muted border-0 focus:ring-2 focus:ring-[hsl(var(--romantic-primary))] outline-none text-sm"
                  autoFocus
                />
              </div>
            </div>

            {/* Country List */}
            <div className="overflow-y-auto max-h-64">
              {filteredCountries.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No countries found
                </div>
              ) : (
                filteredCountries.map((country) => {
                  const code = getPhoneCode(country.iso2);
                  return (
                    <button
                      key={country.id}
                      type="button"
                      onClick={() => handleCountrySelect(country)}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-3 hover:bg-muted transition-colors",
                        selectedCountry?.id === country.id && "bg-muted"
                      )}
                    >
                      <span className="text-xl">{getCountryFlag(country.iso2)}</span>
                      <span className="flex-1 text-left text-sm font-medium">{country.name}</span>
                      <span className="text-sm text-muted-foreground">{code}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p id="phone-error" className="text-xs text-destructive ml-1" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

